import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Check, X, Pencil, Trash2, Users } from "lucide-react";

export default function People() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", relationship: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => { const { data } = await api.get("/people"); setPeople(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const confirm = async (p) => { await api.put(`/people/${p.id}`, { ...p, confirmed: true }); load(); };
  const saveEdit = async () => {
    await api.put(`/people/${editing}`, { ...form, confirmed: true }); setEditing(null); toast.success("Updated"); load();
  };
  const add = async () => {
    if (!form.name.trim()) return;
    await api.post("/people", { ...form, confirmed: true }); setAdding(false); setForm({ name: "", relationship: "", notes: "" }); load();
  };
  const del = async (id) => { if (!window.confirm("Remove this person?")) return; await api.delete(`/people/${id}`); load(); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  const unconfirmed = people.filter((p) => !p.confirmed);
  const confirmed = people.filter((p) => p.confirmed);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-charcoal">People in my story</h1>
        <button data-testid="add-person-btn" onClick={() => { setAdding(true); setForm({ name: "", relationship: "", notes: "" }); }}
          className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm"><Plus size={16} /> Add</button>
      </div>

      {adding && (
        <div className="bg-white rounded-2xl border border-line p-5 space-y-3 animate-fade-up">
          <input data-testid="person-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          <input data-testid="person-rel-input" placeholder="Relationship (e.g. sister, friend)" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          <div className="flex gap-2">
            <button data-testid="person-save-btn" onClick={add} className="bg-coral text-white px-5 py-2 rounded-full font-semibold text-sm">Save</button>
            <button onClick={() => setAdding(false)} className="text-subtle px-3">Cancel</button>
          </div>
        </div>
      )}

      {unconfirmed.length > 0 && (
        <div>
          <p className="text-sm text-sage font-semibold mb-3">A few people to confirm</p>
          <div className="space-y-3">
            {unconfirmed.map((p) => (
              <div key={p.id} data-testid={`confirm-person-${p.id}`} className="bg-sagelight rounded-2xl p-5 animate-fade-up">
                {editing === p.id ? (
                  <div className="space-y-2">
                    <input data-testid={`person-edit-name-${p.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:border-teal" />
                    <input data-testid={`person-edit-rel-${p.id}`} value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Relationship (e.g. sister)" className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:border-teal" />
                    <div className="flex gap-2">
                      <button data-testid={`person-edit-save-${p.id}`} onClick={saveEdit} className="bg-teal text-cream px-4 py-1.5 rounded-full text-sm font-semibold">Save & confirm</button>
                      <button onClick={() => setEditing(null)} className="text-subtle text-sm px-2">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-charcoal">
                      You've mentioned <span className="font-serif font-semibold">{p.name}</span>
                      {p.relationship ? <> — is {p.name} your <span className="font-semibold">{p.relationship}</span>?</> : <>. Who is {p.name} to you?</>}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.relationship && <button data-testid={`person-yes-${p.id}`} onClick={() => confirm(p)} className="inline-flex items-center gap-1.5 bg-teal text-cream px-4 py-2 rounded-full text-sm font-semibold"><Check size={15} /> Yes</button>}
                      <button data-testid={`person-edit-${p.id}`} onClick={() => { setEditing(p.id); setForm({ name: p.name, relationship: p.relationship || "", notes: p.notes || "" }); }} className="inline-flex items-center gap-1.5 bg-white border border-line px-4 py-2 rounded-full text-sm font-medium"><Pencil size={14} /> Edit</button>
                      <button data-testid={`person-no-${p.id}`} onClick={() => del(p.id)} className="inline-flex items-center gap-1.5 text-subtle px-3 py-2 text-sm"><X size={15} /> Not a person</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmed.length === 0 && unconfirmed.length === 0 && (
        <div className="text-center py-16 bg-white rounded-3xl border border-line">
          <Users className="mx-auto text-sage" size={36} />
          <p className="mt-4 text-subtle max-w-md mx-auto">This is where you keep the important people in your story. When we notice a name in a memory, we'll gently suggest adding them here for you to confirm — and you can add anyone yourself at any time with the <b className="text-charcoal">Add</b> button above.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {confirmed.map((p) => (
          <div key={p.id} data-testid={`person-card-${p.id}`} className="bg-white rounded-2xl border border-line p-5 flex items-center justify-between animate-fade-up">
            {editing === p.id ? (
              <div className="w-full space-y-2">
                <input data-testid="person-edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:border-teal" />
                <input data-testid="person-edit-rel-input" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Relationship" className="w-full h-10 px-3 rounded-lg border border-line outline-none focus:border-teal" />
                <div className="flex gap-2"><button data-testid="person-edit-save-btn" onClick={saveEdit} className="bg-coral text-white px-4 py-1.5 rounded-full text-sm">Save</button><button data-testid="person-edit-cancel-btn" onClick={() => setEditing(null)} className="text-subtle text-sm px-2">Cancel</button></div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-teal text-cream flex items-center justify-center font-serif text-lg">{p.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <p className="font-serif text-lg text-charcoal">{p.name}</p>
                    <p className="text-sm text-subtle">{p.relationship || "—"} · {p.memory_count} {p.memory_count === 1 ? "memory" : "memories"}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-person-${p.id}`} onClick={() => { setEditing(p.id); setForm({ name: p.name, relationship: p.relationship || "", notes: p.notes || "" }); }} className="p-2 text-subtle hover:text-teal"><Pencil size={16} /></button>
                  <button data-testid={`delete-person-${p.id}`} onClick={() => del(p.id)} className="p-2 text-subtle hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
